import { authClient } from "@/auth/auth-client";
import {
  trackLoginAttempt,
  trackLoginError,
  trackLoginSuccess,
  trackMagicLinkSent,
} from "@/components/analytics/conversions";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FieldError, FieldGroup } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { revalidateLogic, useForm } from "@tanstack/react-form";
import {
  Link,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { Loader2, Lock, Mail } from "lucide-react";
import { useState } from "react";
import z from "zod";

const loginSchema = z.object({
  email: z.string().email("Geçerli bir email adresi girin"),
  password: z.string().min(1, "Şifre gerekli"),
});

const magicLinkSchema = z.object({
  email: z.string().email("Geçerli bir email adresi girin"),
});

function PasswordLoginForm() {
  const [serverError, setServerError] = useState<string | null>(null);
  const navigate = useNavigate();
  const router = useRouter();
  const search = useSearch({ strict: false });
  const redirect = (search as any)?.redirect as string | undefined;

  const form = useForm({
    defaultValues: { email: "", password: "" },
    validationLogic: revalidateLogic(),
    validators: { onDynamic: loginSchema },
    onSubmit: async ({ value }) => {
      trackLoginAttempt("password");
      setServerError(null);
      const { error } = await authClient.signIn.email({
        email: value.email,
        password: value.password,
      });
      if (error) {
        trackLoginError("password");
        setServerError(error.message || "Giriş yapılırken bir hata oluştu.");
      } else {
        trackLoginSuccess("password");
        await router.invalidate();
        await navigate({ to: redirect || "/" });
      }
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <FieldGroup className="gap-4">
        <form.Field name="email">
          {(field) => (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password-tab-email">Email</Label>
              <Input
                id="password-tab-email"
                type="email"
                placeholder="ornek@email.com"
                autoComplete="email"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
              <FieldError errors={field.state.meta.errors} />
            </div>
          )}
        </form.Field>

        <form.Field name="password">
          {(field) => (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password-tab-password">Şifre</Label>
                <Link
                  to="/password/change"
                  className="text-muted-foreground text-xs underline-offset-4 hover:underline"
                >
                  Şifremi unuttum
                </Link>
              </div>
              <Input
                id="password-tab-password"
                type="password"
                autoComplete="current-password"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
              <FieldError errors={field.state.meta.errors} />
            </div>
          )}
        </form.Field>

        {serverError && (
          <p className="text-destructive text-sm">{serverError}</p>
        )}

        <form.Subscribe selector={(s) => s.isSubmitting}>
          {(isSubmitting) => (
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Giriş yapılıyor...
                </>
              ) : (
                "Giriş Yap"
              )}
            </Button>
          )}
        </form.Subscribe>

        <p className="text-center text-muted-foreground text-sm">
          Hesabın yok mu?{" "}
          <Link
            to="/password/register"
            search={redirect ? { redirect } : undefined}
            className="text-foreground underline underline-offset-4 hover:text-primary"
          >
            Kayıt ol
          </Link>
        </p>
      </FieldGroup>
    </form>
  );
}

function MagicLinkForm() {
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const search = useSearch({ strict: false });
  const redirect = (search as any)?.redirect as string | undefined;

  const form = useForm({
    defaultValues: { email: "" },
    validationLogic: revalidateLogic(),
    validators: { onDynamic: magicLinkSchema },
    onSubmit: async ({ value }) => {
      trackLoginAttempt("magic_link");
      setServerError(null);
      const { error } = await authClient.signIn.magicLink({
        email: value.email,
        callbackURL: redirect || "/",
      });
      if (error) {
        trackLoginError("magic_link");
        setServerError(
          error.message || "Bağlantı gönderilirken bir hata oluştu.",
        );
      } else {
        trackMagicLinkSent();
        setSent(true);
      }
    },
  });

  if (sent) {
    return (
      <div className="flex flex-col items-center gap-4 py-4 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Mail className="h-6 w-6 text-primary" />
        </div>
        <div className="flex flex-col gap-1">
          <p className="font-medium text-sm">Bağlantı Gönderildi</p>
          <p className="text-muted-foreground text-sm">
            Giriş bağlantısı email adresinize gönderildi. Lütfen gelen kutunuzu
            kontrol edin.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="text-muted-foreground text-xs underline underline-offset-4 hover:text-primary"
        >
          Farklı bir adres dene
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <FieldGroup className="gap-4">
        <form.Field name="email">
          {(field) => (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="magic-link-email">Email</Label>
              <Input
                id="magic-link-email"
                type="email"
                placeholder="ornek@email.com"
                autoComplete="email"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
              <FieldError errors={field.state.meta.errors} />
            </div>
          )}
        </form.Field>

        {serverError && (
          <p className="text-destructive text-sm">{serverError}</p>
        )}

        <form.Subscribe selector={(s) => s.isSubmitting}>
          {(isSubmitting) => (
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Gönderiliyor...
                </>
              ) : (
                "Bağlantı Gönder"
              )}
            </Button>
          )}
        </form.Subscribe>

        <p className="text-center text-muted-foreground text-sm">
          Hesabın yok mu?{" "}
          <Link
            to="/password/register"
            search={redirect ? { redirect } : undefined}
            className="text-foreground underline underline-offset-4 hover:text-primary"
          >
            Kayıt ol
          </Link>
        </p>
      </FieldGroup>
    </form>
  );
}

export function LoginForm() {
  const search = useSearch({ strict: false });
  const redirect = (search as any)?.redirect as string | undefined;

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted/40 p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex flex-col items-center gap-2">
          <Link to="/">
            <img
              src="/lightLogo.webp"
              alt="Logo"
              className="h-20 w-20 dark:hidden"
            />
            <img
              src="/darkLogo.webp"
              alt="Logo"
              className="hidden h-20 w-20 dark:block"
            />
          </Link>
        </div>
        <Card className="w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Giriş Yap</CardTitle>
            <CardDescription>Hesabınıza giriş yapın</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Tabs defaultValue="password">
              <TabsList className="mb-4 w-full">
                <TabsTrigger value="password" className="flex-1 gap-1.5">
                  <Lock className="h-3.5 w-3.5" />
                  Şifre ile Giriş
                </TabsTrigger>
                <TabsTrigger value="magic" className="flex-1 gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  Bağlantı ile Giriş
                </TabsTrigger>
              </TabsList>
              <TabsContent value="password">
                <PasswordLoginForm />
              </TabsContent>
              <TabsContent value="magic">
                <MagicLinkForm />
              </TabsContent>
            </Tabs>
            <div className="flex items-center gap-2">
              <Separator className="flex-1" />
              <span className="text-muted-foreground text-xs">veya</span>
              <Separator className="flex-1" />
            </div>
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => {
                trackLoginAttempt("google");
                authClient.signIn.social({
                  provider: "google",
                  callbackURL: redirect || "/",
                });
              }}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google ile Giriş Yap
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
